/**
 * Pure camera arithmetic for WorldScene (owner: env-builder). Everything returns integers so the
 * camera scroll is always pixel-snapped; no Phaser import.
 */
export interface Vec2 { x: number; y: number }

/** Clamp a scroll position to the world; when the world is smaller than the view, centre it. */
export function clampScroll(x: number, y: number, viewW: number, viewH: number, worldW: number, worldH: number): Vec2 {
  const cx = worldW <= viewW ? (worldW - viewW) / 2 : Math.min(Math.max(x, 0), worldW - viewW);
  const cy = worldH <= viewH ? (worldH - viewH) / 2 : Math.min(Math.max(y, 0), worldH - viewH);
  return { x: Math.round(cx), y: Math.round(cy) };
}

/** Scroll that centres the view on a world point (then clamped). Dead-zone 0. */
export function followScroll(targetX: number, targetY: number, viewW: number, viewH: number, worldW: number, worldH: number): Vec2 {
  return clampScroll(Math.round(targetX - viewW / 2), Math.round(targetY - viewH / 2), viewW, viewH, worldW, worldH);
}

export function easeInOutCubic(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return c < 0.5 ? 4 * c * c * c : 1 - Math.pow(-2 * c + 2, 3) / 2;
}

/** Position along a pan between two scroll points at linear progress `t` (eased internally). */
export function panAt(from: Vec2, to: Vec2, t: number): Vec2 {
  const e = easeInOutCubic(t);
  return { x: Math.round(from.x + (to.x - from.x) * e), y: Math.round(from.y + (to.y - from.y) * e) };
}

/**
 * Shake offset for one step. `intensity` < 1 is a fraction of the viewport (Phaser convention);
 * >= 1 is pixels. Amplitude decays linearly with `remaining` (1 → 0).
 */
export function shakeOffset(rnd: () => number, intensity: number, remaining: number, viewW: number, viewH: number): Vec2 {
  const ampX = intensity >= 1 ? intensity : intensity * viewW;
  const ampY = intensity >= 1 ? intensity : intensity * viewH;
  const k = Math.max(0, Math.min(1, remaining));
  return { x: Math.round((rnd() * 2 - 1) * ampX * k), y: Math.round((rnd() * 2 - 1) * ampY * k) };
}
